import { useState } from 'react';

import type { BaselineImportResponse, ContractResponse } from '../../api';
import {
  AsyncSection,
  DataTable,
  FileField,
  Form,
  FormError,
  PageHeader,
  Panel,
  SubmitButton,
  TextField,
  useNotifications,
  type Column,
} from '../../components';
import { formatDate, formatDateTime, formatInteger } from '../../format';
import { useRequiredSession } from '../../session';
import { saveAsAttachment } from '../../utils/download';
import { useBaselineImportSource, useBaselineImports, useImportBaseline } from '../queries';
import {
  BASELINE_COPY,
  SPREADSHEET_ACCEPT,
  isFormatRefusal,
  isMovedOnRefusal,
  rowProblemsOf,
  serverSentence,
  type RowProblem,
} from './baselineImport';

/**
 * Loading a contract's pre-existing population, once.
 *
 * A sub-view of the company detail rather than a route of its own, for the
 * reason the detail itself is one: onboarding is a single task worked straight
 * down, and a page load in the middle of it is a page load in the middle of
 * somebody's afternoon. It sits behind the staff guard because its route does
 * -- and behind `contract:write` on the server, which no customer role holds.
 *
 * The screen is shaped by three refusals rather than by the happy path, because
 * the happy path is one button:
 *
 *  - **A bad row rejects the whole file**, so the rejected rows are rendered on
 *    the page, line by line, and stay there while the analyst fixes the file.
 *    Not a toast: a toast is gone before you have found the file.
 *  - **A contract that has moved on is refused**, and the override appears only
 *    then. It is never a checkbox somebody could have ticked in advance, and it
 *    is never on the first request -- see `useImportBaseline`, which omits the
 *    field entirely unless it is true.
 *  - **The source file is CPFs and salaries.** It downloads as an attachment
 *    under its stored name and is never rendered in the page, unlike the
 *    review-queue document, which is PDFs and images and is meant to be read.
 */

const PROBLEM_COLUMNS: readonly Column<RowProblem>[] = [
  { key: 'line', header: BASELINE_COPY.lineHeader, align: 'end', render: (row) => row.line },
  { key: 'column', header: BASELINE_COPY.columnHeader, render: (row) => row.column },
  { key: 'reason', header: BASELINE_COPY.reasonHeader, render: (row) => row.reason },
];

export function BaselineImportPage({
  contract,
  companyName,
  onBack,
}: {
  contract: ContractResponse;
  companyName: string;
  onBack: () => void;
}) {
  const session = useRequiredSession();
  const history = useBaselineImports(contract.id, contract.company_id);
  const runImport = useImportBaseline();
  const source = useBaselineImportSource();
  const { notify } = useNotifications();

  const [file, setFile] = useState<File | null>(null);
  const [baselineDate, setBaselineDate] = useState('');
  const [override, setOverride] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  // Whether the override step is on screen is a property of the last answer
  // from the server, not a flag the screen keeps: changing the file or the date
  // clears the error, which takes the checkbox away with it. There is no state
  // in which a stale confirmation could be carried into a different request.
  const problems = rowProblemsOf(runImport.error);
  const askedToOverride = isMovedOnRefusal(runImport.error);
  const rejectedWholeFile = isFormatRefusal(runImport.error);

  const startOver = () => {
    setOverride(false);
    setLocalError(null);
    runImport.reset();
  };

  const chooseFile = (chosen: File | null) => {
    setFile(chosen);
    startOver();
  };

  const chooseDate = (chosen: string) => {
    setBaselineDate(chosen);
    startOver();
  };

  const submit = () => {
    if (file === null) {
      setLocalError(BASELINE_COPY.missingFile);
      return;
    }
    if (baselineDate === '') {
      setLocalError(BASELINE_COPY.missingDate);
      return;
    }
    setLocalError(null);
    runImport.mutate(
      {
        contractId: contract.id,
        companyId: contract.company_id,
        baselineDate,
        file,
        // `false` and "not asked" are the same request to the API and are not
        // the same thing here, so the flag only ever travels as `true`.
        ...(override && { override: true }),
      },
      {
        onSuccess: () => {
          setOverride(false);
          notify('success', BASELINE_COPY.imported);
        },
      },
    );
  };

  const download = (record: BaselineImportResponse) => {
    source.mutate(
      {
        contractId: contract.id,
        companyId: contract.company_id,
        baselineImportId: record.id,
      },
      { onSuccess: (blob) => saveAsAttachment(blob, record.source_filename) },
    );
  };

  /**
   * Who ran an import.
   *
   * The record carries the staff user's identifier and nothing else -- names
   * are not denormalised onto it, and resolving one would mean a second request
   * for a column. So the identifier is what is shown, except where the screen
   * already knows the answer: the person reading it.
   */
  const whoRan = (record: BaselineImportResponse) => {
    if (record.imported_by_user_id === null) {
      return BASELINE_COPY.unknownUser;
    }
    if (record.imported_by_user_id === session.principal.id) {
      return session.principal.display_name;
    }
    return <code>{record.imported_by_user_id}</code>;
  };

  const historyColumns: readonly Column<BaselineImportResponse>[] = [
    {
      key: 'imported_at',
      header: BASELINE_COPY.whenHeader,
      render: (row) => formatDateTime(row.imported_at),
    },
    { key: 'imported_by', header: BASELINE_COPY.whoHeader, render: whoRan },
    {
      key: 'baseline_date',
      header: BASELINE_COPY.baselineDateHeader,
      render: (row) => formatDate(row.baseline_date),
    },
    {
      key: 'row_count',
      header: BASELINE_COPY.rowCountHeader,
      align: 'end',
      render: (row) => formatInteger(row.row_count),
    },
    {
      key: 'overridden',
      header: BASELINE_COPY.overrideHeader,
      render: (row) => (row.overridden ? BASELINE_COPY.overrideYes : BASELINE_COPY.overrideNo),
    },
    {
      key: 'source',
      header: BASELINE_COPY.sourceHeader,
      align: 'end',
      render: (row) => (
        <button
          type="button"
          className="button button--ghost"
          disabled={source.isPending}
          onClick={() => download(row)}
        >
          {source.isPending ? BASELINE_COPY.downloading : BASELINE_COPY.download}
        </button>
      ),
    },
  ];

  return (
    <div className="page">
      <PageHeader
        title={BASELINE_COPY.title}
        description={`${BASELINE_COPY.description} Contrato de ${companyName} com ${contract.insurer.name}.`}
        actions={
          <button type="button" className="button button--ghost" onClick={onBack}>
            {BASELINE_COPY.back}
          </button>
        }
      />

      <Panel title={BASELINE_COPY.formTitle} description={BASELINE_COPY.formDescription}>
        <Form onSubmit={submit} aria-label={BASELINE_COPY.formTitle}>
          {/* The two refusals below get panels of their own, so the generic
              sentence at the top of the form would be a second, vaguer copy of
              what is already on screen. */}
          {!askedToOverride && !rejectedWholeFile && problems.length === 0 && (
            <FormError error={runImport.error} />
          )}
          {localError !== null && (
            <div className="form__error" role="alert">
              <p>{localError}</p>
            </div>
          )}
          <FileField
            label={BASELINE_COPY.fileLabel}
            hint={BASELINE_COPY.fileHint}
            accept={SPREADSHEET_ACCEPT}
            required
            onChange={chooseFile}
          />
          <TextField
            label={BASELINE_COPY.baselineDateLabel}
            hint={BASELINE_COPY.baselineDateHint}
            type="date"
            value={baselineDate}
            onChange={chooseDate}
            required
          />
          {askedToOverride && (
            <div className="field">
              <label className="field__label" htmlFor="baseline-override">
                <input
                  id="baseline-override"
                  type="checkbox"
                  checked={override}
                  onChange={(event) => setOverride(event.target.checked)}
                />{' '}
                {BASELINE_COPY.overrideLabel}
              </label>
              {!override && <p className="field__hint">{BASELINE_COPY.overrideHint}</p>}
            </div>
          )}
          <div className="form__actions">
            {/* An unconfirmed override leaves the button disabled under its own
                label, so the thing that is missing is the tick and not the
                button. `pending` is what `SubmitButton` calls disabled. */}
            <SubmitButton
              pending={runImport.isPending || (askedToOverride && !override)}
              pendingLabel={
                runImport.isPending ? BASELINE_COPY.submitting : BASELINE_COPY.overrideSubmit
              }
            >
              {askedToOverride ? BASELINE_COPY.overrideSubmit : BASELINE_COPY.submit}
            </SubmitButton>
          </div>
        </Form>
      </Panel>

      {askedToOverride && (
        <Panel title={BASELINE_COPY.conflictTitle} description={BASELINE_COPY.conflictDescription}>
          <p className="panel__footnote">
            {BASELINE_COPY.serverSaid} <q>{serverSentence(runImport.error)}</q>
          </p>
        </Panel>
      )}

      {rejectedWholeFile && (
        <Panel title={BASELINE_COPY.formatTitle} description={BASELINE_COPY.formatDescription}>
          <p className="panel__footnote">
            {BASELINE_COPY.serverSaid} <q>{serverSentence(runImport.error)}</q>
          </p>
        </Panel>
      )}

      {problems.length > 0 && (
        <Panel title={BASELINE_COPY.rejectedTitle} description={BASELINE_COPY.rejectedDescription}>
          <DataTable
            caption={BASELINE_COPY.rejectedCaption}
            columnVisibility={false}
            columns={PROBLEM_COLUMNS}
            rows={problems}
            rowKey={(row, index) => `${row.line}-${index}`}
          />
        </Panel>
      )}

      <Panel title={BASELINE_COPY.historyTitle} description={BASELINE_COPY.historyDescription}>
        <FormError error={source.error} />
        <AsyncSection
          state={history}
          loadingLabel={BASELINE_COPY.historyLoading}
          errorTitle={BASELINE_COPY.historyError}
          isEmpty={(data) => data.items.length === 0}
          emptyTitle={BASELINE_COPY.historyEmpty}
          emptyDescription={BASELINE_COPY.historyEmptyDescription}
        >
          {(data) => (
            <DataTable
              id="baseline-imports"
              caption={BASELINE_COPY.historyCaption}
              columns={historyColumns}
              rows={data.items}
              rowKey={(row) => row.id}
            />
          )}
        </AsyncSection>
      </Panel>
    </div>
  );
}
